import { getUuid } from '@sibe/utilities/common';
import { HsCameraType } from '../engine-new/enums/camera-type.enum';
import type { HsRenderObjects } from '../engine-new/filtering/render-objects.class';
import { HsPerspectiveCamera } from '../engine-new/objects/cameras/perspective-camera.class';
import type { HsGraphicLayer } from '../graphic-render-new/objects/graphic-layer.class';
import type { HsGraphicObject } from '../graphic-render-new/objects/graphic-object.class';
import type { HsGraphicQueue } from '../graphic-render-new/objects/graphic-queue.class';
import { HsRaycastOutput } from '../graphic-render-new/objects/raycast-output.class';
import type { HsClippingStorage } from '../engine-new/storages/clipping-storage.class';
import { HsRaycast } from '../graphic-render-new/raycast.class';
import { HsRender } from '../graphic-render-new/render.class';
import { HsHypersceneEngineConstants } from '../engine-new/constants/hyperscene-engine-constants.global';
import { HsHypersceneMathUtils } from '../math-new/functions/hyperscene-math-utils.class';
import { HsMatrix4 } from '../math-new/objects/matrix4.class';
import { HsVector3 } from '../math-new/objects/vector3.class';
import { HsGlobal } from '../tools-new/global';
import { HsCameraToolTypeControlAction } from '../tools-new/actions/camera-tool-type-control-action.class';
import { HsCameraToolType } from '../tools-new/actions/camera-tool-type.enum';
import { HsCameraTypeControlAction } from '../tools-new/actions/camera-type-control-action.class';
import { HsCameraToolsHandler } from '../tools-new/camera/camera-tools-handler.class';
import type { HsControlActionsHandler } from '../tools-new/camera/control-actions-handler.class';
import { hsSortByCameraDistance } from './culling/camera-distance-sort.function';
import type { HsDisposable } from './extensions/disposable.interface';
import { HsPluginHost } from './extensions/plugin-host.class';
import type { HsViewerHandle } from './extensions/viewer-handle.interface';
import type { HsViewerPlugin } from './extensions/viewer-plugin.interface';
import {
  HsCullingPass,
  type HsCullingFilter,
  type HsCullingSorter,
  type HsCullingSpans,
} from './runtime/culling-pass.class';
import { HsRenderLoop, type HsAnimationFrames } from './runtime/render-loop.class';
import { HsViewerSurface } from './runtime/viewer-surface.class';

type HsVisibleObjects = HsRenderObjects<HsGraphicObject, HsGraphicLayer>;

export interface HsHypersceneViewerOptions {
  canvas: HTMLCanvasElement;
  /** Camera to view the scene through (default a new perspective camera). */
  camera?: HsPerspectiveCamera;
  /** MSAA samples of the scene layer (default 4). */
  antialiasingSamples?: 0 | 2 | 4 | 8;
  /** Redraw every animation frame instead of on demand (default false). */
  continuous?: boolean;
  /** Draw the scene over several frames within the render's frame budget (default false). */
  distributedRendering?: boolean;
  /** RGBA clear color (default light grey). */
  clearColor?: readonly [number, number, number, number];
  /** Decides which queue objects are visible; the default tests bounding spheres against the frustum. */
  filter?: HsCullingFilter<HsGraphicQueue, HsPerspectiveCamera, HsVisibleObjects>;
  /** Orders visible objects; the default draws opaque objects near to far and transparent ones far to near. */
  sorter?: HsCullingSorter<HsPerspectiveCamera, HsVisibleObjects>;
  /** Animation frame functions (default the global ones). */
  frames?: HsAnimationFrames;
  /** Follow the canvas box with a ResizeObserver (default true). */
  autoResize?: boolean;
  /** A WebGL 2 context of `canvas` to draw with (default a new one without antialiasing). */
  gl?: WebGL2RenderingContext;
  /** The renderer to draw with (default a new one). */
  render?: HsRender;
  /** Camera tools for `camera`; a host that passes them initializes them itself (default new, initialized ones). */
  cameraTools?: HsCameraToolsHandler;
  /**
   * Least time between culling passes, in ms. A culling request inside the interval waits for it, which
   * keeps culling large queues off most frames while the camera moves (default 0: cull every changed frame).
   */
  cullingIntervalMs?: number;
  /** Time a distributed frame may draw, in ms after the frame starts (default no deadline). */
  frameBudgetMs?: number;
  /** True while the user drives the camera (button held, touch); frames keep drawing meanwhile. */
  isNavigating?: () => boolean;
  /** Keep the previous visible lists when culling finds the same objects, so draw batches stay valid. */
  reuseUnchangedLists?: () => boolean;
  /** Timing hooks for the culling pass, or null while profiling is off; `HsViewerProfiler` fits them. */
  spans?: () => HsCullingSpans<unknown> | null;
  /** Host work at fixed points of each frame. */
  hooks?: HsViewerFrameHooks;
}

/** The object under a canvas point and the world point hit on it. */
export interface HsViewerPick {
  object: HsGraphicObject;
  /** Instance of a collection (instanced batch) that was hit, or null. */
  instanceIndex: number | null;
  /** World position of the hit. */
  point: [number, number, number];
}

/** What a frame hook learns about the frame being drawn. */
export interface HsViewerFrameState {
  /** `performance.now()` at the frame start. */
  startedAt: number;
  cameraMoved: boolean;
  /** The camera moved this frame or the user is navigating. */
  navigating: boolean;
}

/** Host work at fixed points of a frame, in this order. */
export interface HsViewerFrameHooks {
  /** After the camera updated and before culling; streaming and LOD updates go here. */
  beforeCulling?(frame: HsViewerFrameState): void;
  /** After a culling pass; `listsChanged` tells whether the visible objects changed. */
  afterCulling?(frame: HsViewerFrameState, listsChanged: boolean): void;
  /** After clearing and before drawing; per-frame render settings go here. */
  beforeDraw?(frame: HsViewerFrameState): void;
  /** After drawing; `complete` is false while distributed rendering has objects left. */
  afterDraw?(frame: HsViewerFrameState, complete: boolean): void;
  /** Return true to draw another frame although nothing changed (animations, batch preparation). */
  keepDrawing?(frame: HsViewerFrameState): boolean;
}

/** Visible when the bounding sphere meets the frustum; transparent materials go to the sorted list. */
export const HS_DEFAULT_VIEWER_FILTER: HsCullingFilter<HsGraphicQueue, HsPerspectiveCamera, HsVisibleObjects> = {
  filter(queue: HsGraphicQueue, camera: HsPerspectiveCamera, target: HsVisibleObjects): void {
    for (const object of queue.objects.values()) {
      if (!camera.frustum.hasIntersectionWithSphere(object.boundingSphere)) {
        continue;
      }
      if (object.material.getShader().hasTransparency) {
        target.sortedObjects.push(object);
      } else {
        target.unsortedObjects.push(object);
      }
    }
  },
};

/** Opaque objects near to far (early depth rejection), transparent ones far to near (blending). */
export const HS_DEFAULT_VIEWER_SORTER: HsCullingSorter<HsPerspectiveCamera, HsVisibleObjects> = {
  sort(camera: HsPerspectiveCamera, target: HsVisibleObjects): void {
    hsSortByCameraDistance(target.sortedObjects, camera.worldMatrix, true);
    hsSortByCameraDistance(target.unsortedObjects, camera.worldMatrix, false);
  },
};

/**
 * A ready viewer on a canvas: WebGL setup, a perspective camera with the camera tools, frustum culling,
 * on-demand or continuous drawing and plugins. Add graphic objects to `queue`; feed input to `controls`
 * (or attach an `HsViewerInput`).
 */
export class HsHypersceneViewer implements HsViewerHandle, HsDisposable {
  public readonly gl: WebGL2RenderingContext;
  public readonly camera: HsPerspectiveCamera;
  public readonly cameraTools: HsCameraToolsHandler;
  public readonly render: HsRender;
  public readonly surface: HsViewerSurface;
  public readonly plugins: HsPluginHost = new HsPluginHost(this);
  public distributedRendering: boolean;
  public continuous: boolean;
  /** Layers drawn over the scene (HUDs, gizmos); `resize` resizes them with the scene layer. */
  public readonly overlayLayers: HsGraphicLayer[] = [];

  private readonly loop: HsRenderLoop;
  private readonly culling: HsCullingPass<HsGraphicObject, HsGraphicQueue, HsPerspectiveCamera, HsVisibleObjects>;
  private readonly lastCameraMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  private readonly frameListeners: Set<() => void> = new Set();
  private readonly clearColor: readonly [number, number, number, number];
  private readonly cullingIntervalMs: number;
  private readonly resizeObserver: ResizeObserver | null = null;
  private readonly hooks: HsViewerFrameHooks;
  private raycast: HsRaycast | null = null;
  /** Clipping planes `pick` honors; set it to the scene's planes so picks skip what a section cut away. */
  public pickClipping: HsClippingStorage | null = null;
  private cullingDirty: boolean = true;
  private cullingAt: number = Number.NEGATIVE_INFINITY;
  private drawCursor: number = 0;
  private disposed: boolean = false;

  public constructor(private readonly options: HsHypersceneViewerOptions) {
    const gl: WebGL2RenderingContext | null =
      options.gl ?? options.canvas.getContext('webgl2', { preserveDrawingBuffer: true, antialias: false });
    if (gl === null) {
      throw new Error('WebGL 2 is not available on this canvas.');
    }
    this.gl = gl;
    this.camera = options.camera ?? new HsPerspectiveCamera(getUuid(), 'camera');
    this.render = options.render ?? new HsRender();
    this.cameraTools = options.cameraTools ?? new HsCameraToolsHandler(this.camera);
    this.hooks = options.hooks ?? {};
    this.continuous = options.continuous ?? false;
    this.distributedRendering = options.distributedRendering ?? false;
    this.clearColor = options.clearColor ?? [0.75, 0.75, 0.75, 1];
    this.cullingIntervalMs = options.cullingIntervalMs ?? 0;
    this.surface = new HsViewerSurface(options.canvas, gl, this.camera, this.render, {
      antialiasingSamples: options.antialiasingSamples ?? 4,
    });
    this.culling = new HsCullingPass(
      options.filter ?? HS_DEFAULT_VIEWER_FILTER,
      options.sorter ?? HS_DEFAULT_VIEWER_SORTER
    );
    this.loop = new HsRenderLoop(() => this.frame(), options.frames ?? globalThis);
    this.camera.updateProjectionMatrix();
    this.camera.updateLocalMatrix();
    this.cameraTools.updateCanvasSize(this.surface.canvasSize);
    if (options.cameraTools === undefined) {
      this.cameraTools.initialize();
      this.controls.updateControlAction(new HsCameraToolTypeControlAction(HsCameraToolType.Default));
      this.controls.updateControlAction(new HsCameraTypeControlAction(HsCameraType.Perspective));
    }
    if ((options.autoResize ?? true) && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(options.canvas);
    }
    this.requestRender();
  }

  /** Where camera input goes: mouse, wheel, keyboard and touch actions. */
  public get controls(): HsControlActionsHandler {
    return this.cameraTools.controlActionsHandler;
  }

  /** The scene's graphic objects. */
  public get queue(): HsGraphicQueue {
    return this.surface.objectsQueue;
  }

  /** A culling pass is waiting (`invalidate` or a camera move) and runs on the next allowed frame. */
  public get cullingPending(): boolean {
    return this.cullingDirty;
  }

  /** `performance.now()` of the last culling pass. */
  public get lastCullingAt(): number {
    return this.cullingAt;
  }

  /** Index of the next object distributed rendering draws; 0 after a restart. */
  public get drawnObjects(): number {
    return this.drawCursor;
  }

  /** Starts distributed rendering over from the first visible object. */
  public restartDrawing(): void {
    this.drawCursor = 0;
    this.requestRender();
  }

  /** The objects the last culling pass found visible. */
  public get visibleObjects(): HsVisibleObjects {
    return this.surface.renderObjects;
  }

  public use<T extends HsViewerPlugin>(plugin: T): T {
    return this.plugins.use(plugin);
  }

  /** Adds an object to the scene and redraws. */
  public add(object: HsGraphicObject): void {
    this.queue.addObject(object);
    this.invalidate();
  }

  /**
   * Re-runs culling on the next frame, for example after objects moved or changed visibility. `immediately`
   * skips the wait for `cullingIntervalMs`.
   */
  public invalidate(immediately: boolean = false): void {
    this.cullingDirty = true;
    if (immediately) {
      this.cullingAt = Number.NEGATIVE_INFINITY;
    }
    this.requestRender();
  }

  /**
   * Treats the camera's current placement as already drawn: the next frame does not count it as a camera
   * move. Hosts call it after placing the camera themselves when they handle the redraw.
   */
  public syncCamera(): void {
    this.lastCameraMatrix.copy(this.camera.worldMatrix);
  }

  public requestRender(): void {
    if (!this.disposed) {
      this.loop.request();
    }
  }

  public onFrame(listener: () => void): HsDisposable {
    this.frameListeners.add(listener);
    return { dispose: (): void => void this.frameListeners.delete(listener) };
  }

  /**
   * Looks at the box `min`..`max` from above one corner, far enough to see all of it, and orbits around its
   * center. Hosts call it after opening a scene, for example with `HsViewerApi.getSceneBoundingBox`.
   */
  public frameBox(box: { min: ArrayLike<number>; max: ArrayLike<number> }): void {
    const center: HsVector3 = new HsVector3(
      (box.min[0] + box.max[0]) / 2,
      (box.min[1] + box.max[1]) / 2,
      (box.min[2] + box.max[2]) / 2
    );
    const span: number = Math.max(box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]);
    if (!Number.isFinite(span) || span < 0) {
      return;
    }
    const distance: number = Math.max(span, 10);
    const eye: HsVector3 = new HsVector3(center.x + distance, center.y + distance * 0.5, center.z + distance);
    this.camera.far = Math.max(this.camera.far, distance * 6);
    this.camera.worldMatrix.setPosition(eye.values);
    HsHypersceneMathUtils.lookAt(this.camera.worldMatrix, eye, center, HsHypersceneEngineConstants.UP);
    this.camera.updateProjectionMatrix();
    this.camera.updateCameraMatrixInverse();
    HsGlobal.ROTATE_POINT.copy(center);
    this.invalidate();
  }

  /**
   * The visible object under the client (CSS pixel) point and the world point hit on it, or null. Picking
   * draws the visible objects once into an ID buffer, so call it on clicks rather than every frame.
   */
  public pick(clientX: number, clientY: number): HsViewerPick | null {
    const canvas: HTMLCanvasElement = this.surface.canvas;
    const bounds: DOMRect = canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return null;
    }
    const x: number = (clientX - bounds.left) * (canvas.width / bounds.width);
    const y: number = (bounds.bottom - clientY) * (canvas.height / bounds.height);
    if (this.raycast === null) {
      this.raycast = new HsRaycast();
      this.raycast.layers = [this.surface.objectsLayer];
    }
    this.raycast.clippingStorage = this.pickClipping;
    const output: HsRaycastOutput = new HsRaycastOutput();
    this.raycast.draw(this.gl, this.render, this.camera, this.visibleObjects, [x, y]);
    this.raycast.readRaycast(this.gl, x, y, output);
    if (output.object === null) {
      return null;
    }
    return {
      object: output.object,
      instanceIndex: output.instanceIndex,
      point: [output.position.x, output.position.y, output.position.z],
    };
  }

  /** Follows a change of the canvas box. */
  public resize(): void {
    this.cameraTools.updateCanvasSize(this.surface.resize(this.render, this.overlayLayers));
    this.invalidate();
  }

  /**
   * Stops drawing, uninstalls plugins and releases the WebGL context with every GPU resource in it. The
   * canvas cannot host another viewer afterwards; create a new canvas for one.
   */
  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.loop.cancel();
    this.resizeObserver?.disconnect();
    this.frameListeners.clear();
    this.plugins.dispose();
    this.cameraTools.dispose();
    this.render.clearMeshBatches();
    // Browsers cap live WebGL contexts per page; an app that remounts viewers must not keep old ones.
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  private frame(): void {
    const gl: WebGL2RenderingContext = this.gl;
    const startedAt: number = performance.now();
    this.cameraTools.loop();
    this.camera.updateCameraMatrixInverse();
    this.camera.updateProjectionMatrix();
    // Camera tools keep moving the camera after input ends (inertia); keep drawing until it settles.
    const cameraMoved: boolean = !this.camera.worldMatrix.equals(this.lastCameraMatrix, 0.001);
    const frame: HsViewerFrameState = {
      startedAt,
      cameraMoved,
      navigating: cameraMoved || this.options.isNavigating?.() === true,
    };
    this.hooks.beforeCulling?.(frame);
    if (cameraMoved) {
      this.lastCameraMatrix.copy(this.camera.worldMatrix);
      this.cullingDirty = true;
      this.drawCursor = 0;
    }
    if (this.cullingDirty && startedAt - this.cullingAt >= this.cullingIntervalMs) {
      this.cullingDirty = false;
      this.cullingAt = startedAt;
      const listsChanged: boolean = this.culling.run(
        this.queue,
        this.camera,
        this.visibleObjects,
        this.options.reuseUnchangedLists?.() === true,
        this.options.spans?.() ?? null
      );
      if (listsChanged) {
        this.drawCursor = 0;
      }
      this.hooks.afterCulling?.(frame, listsChanged);
    }
    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3]);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
    this.hooks.beforeDraw?.(frame);
    const budgetMs: number | undefined = this.options.frameBudgetMs;
    this.drawCursor = this.render.drawToFramebuffer(
      gl,
      this.camera,
      this.visibleObjects,
      this.distributedRendering ? Math.max(0, this.drawCursor) : undefined,
      false,
      frame.navigating,
      budgetMs === undefined ? undefined : startedAt + budgetMs
    );
    const total: number = this.visibleObjects.unsortedObjects.length + this.visibleObjects.sortedObjects.length;
    const complete: boolean = !this.distributedRendering || this.drawCursor >= total;
    this.hooks.afterDraw?.(frame, complete);
    this.frameListeners.forEach((listener: () => void) => listener());
    if (
      this.continuous ||
      frame.navigating ||
      !complete ||
      this.cullingDirty ||
      this.hooks.keepDrawing?.(frame) === true
    ) {
      this.requestRender();
    }
  }
}
